import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CommonServicePIM } from '../../Interfaces/PIMs';
import { SD_SPNameList } from '../../Utility/StaticDatas';
import { getAuthToken, removeAuthToken } from '../../Helper/authToken';

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_APIBaseUrl}/AuthDataGet/ExecuteJson/${SD_SPNameList.DB_SP_CommonServices}`,
  prepareHeaders: (headers) => {
    const token = getAuthToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    headers.set('Content-Type', 'application/json');
    return headers;
  },
});

const SPCommonServicesApi = createApi({
  reducerPath: 'SPCommonServicesApi',
  baseQuery: async (args, api, extraOptions) => {
    try {
      const result = await baseQuery(args, api, extraOptions);
      return result;
    } catch (error: any) {
      if (error.status === 401) {
        removeAuthToken();
        window.location.replace('/auth/login');
      }
      throw error;
    }
  },
  tagTypes: ['CommonService'],
  endpoints: (builder) => ({
    getAllTermList: builder.query({
      query: () => ({
        url: '1',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getAllInternalServiceTypeList: builder.query({
      query: () => ({
        url: '2',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getAllCourseList: builder.query({
      query: () => ({
        url: '3',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getAllServiceCourseDetailTypeList: builder.query({
      query: () => ({
        url: '4',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getPackageList: builder.query({
      // filter by free package / paid / all
      query: (data: { IsFree: boolean } | null) => ({
        url: '5',
        method: 'POST',
        body: data !== null ? { IsFree: data.IsFree } : {},
      }),
      providesTags: ['CommonService'],
    }),
    getClientLibraryLinks: builder.query({
      query: (data) => ({
        url: '6',
        method: 'POST',
        body: data,
      }),
      providesTags: ['CommonService'],
    }),
    getClientSection: builder.query({
      query: (data) => ({
        url: '7',
        method: 'POST',
        body: data,
      }),
      providesTags: ['CommonService'],
    }),
    getFacultyTemplates: builder.query({
      query: () => ({
        url: '8',
        method: 'POST',
        body: {},
      }),
    }),
    getParentChapterList: builder.query({
      query: () => ({
        url: '9',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getPackageListWithCombo: builder.query({
      // filter by package with / without / only combo
      query: (data: { IsCombo: boolean; IsFree?: boolean } | null) => ({
        url: '10',
        method: 'POST',
        body: data !== null ? { IsCombo: data.IsCombo } : {},
      }),
      providesTags: ['CommonService'],
    }),
    getApiTypeList: builder.query({
      query: () => ({
        url: '11',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getFranchiseList: builder.query({
      query: () => ({
        url: '12',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getLayoutStoreList: builder.query({
      query: () => ({
        url: '13',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getEmailServiceProvideList: builder.query({
      query: () => ({
        url: '14',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getEmailServiceProviderSuggestions: builder.query({
      query: (data) => ({
        url: '15',
        method: 'POST',
        body: data,
      }),
      providesTags: ['CommonService'],
    }),
    getTeacherList: builder.query({
      query: () => ({
        url: '16',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getPackageListOfSpecificService: builder.query({
      query: (data: CommonServicePIM) => ({
        url: '17',
        method: 'POST',
        body: data,
      }),
      providesTags: ['CommonService'],
    }),
    getAllPlayer: builder.query({
      query: () => ({
        url: '18',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getAllMcqQuestionType: builder.query({
      query: () => ({
        url: '19',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),

    getAllInternalServicesByType: builder.query({
      query: (data: CommonServicePIM) => ({
        url: '20',
        method: 'POST',
        body: data,
      }),
      providesTags: ['CommonService'],
    }),
    getAllTherySegment: builder.query({
      query: () => ({
        url: '21',
        method: 'POST',
        body: {},
      }),
    }),
    getAllCountryCodeList: builder.query({
      query: () => ({
        url: '22',
        method: 'POST',
        body: {},
        providesTags: ['CommonService'],
      }),
    }),
    getAllStore: builder.query({
      query: () => ({
        url: '23',
        method: 'POST',
        body: {},
        providesTags: ['CommonService'],
      }),
    }),
    getAllPosition: builder.query({
      query: () => ({
        url: '24',
        method: 'POST',
        body: {},
        providesTags: ['CommonService'],
      }),
    }),
    getFacultyList: builder.query({
      query: () => ({
        url: '25',
        method: 'POST',
        body: {},
        providesTags: ['CommonService'],
      }),
    }),
    getMediaList: builder.query({
      query: () => ({
        url: '26',
        method: 'POST',
        body: {},
        providesTags: ['CommonService'],
      }),
    }),
    getAllExistingHostByFranchiseId: builder.query({
      query: () => ({
        url: '27',
        method: 'POST',
        body: {},
        providesTags: ['CommonService'],
      }),
    }),
    getAllMCQSetDropdown: builder.query({
      query: (data: CommonServicePIM) => ({
        url: '28',
        method: 'POST',
        body: data,
      }),
      providesTags: ['CommonService'],
    }),
    getDashboardCardListDropdown: builder.query({
      query: () => ({
        url: '29',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getNotificationType: builder.query({
      query: () => ({
        url: '30',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getLockscreenType: builder.query({
      query: () => ({
        url: '31',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getContentManagementType: builder.query({
      query: () => ({
        url: '32',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getAllCommonEmailTemplate: builder.query({
      query: () => ({
        url: '33',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getAllVideoTypeDropdownList: builder.query({
      query: () => ({
        url: '34',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getNotesType: builder.query({
      query: () => ({
        url: '35',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    clientDeployLinkList: builder.query({
      query: () => ({
        url: '36',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    packageDropdownPerCourse: builder.query({
      query: (CourseId: number | string) => ({
        url: '37',
        method: 'POST',
        body: {
          CourseId,
        },
      }),
      providesTags: ['CommonService'],
    }),
    packageEmailTemplate: builder.query({
      query: () => ({
        url: '38',
        method: 'POST',
        body: {},
      }),
      providesTags: ['CommonService'],
    }),
    getChapterByCourseId: builder.query({
      query: (CourseId: number) => ({
        url: '39',
        method: 'POST',
        body: { CourseId },
      }),
      providesTags: ['CommonService'],
    }),
    getChapterByPackageId: builder.query({
      query: (PackageId: number) => ({
        url: '40',
        method: 'POST',
        body: { PackageId },
      }),
      providesTags: ['CommonService'],
    }),
    packageDropdownByCourseId: builder.query({
      query: (data: { CourseId: number; IsCombo?: boolean }) => ({
        url: '41',
        method: 'POST',
        body: data,
      }),
      providesTags: ['CommonService'],
    }),
    packageDropdownByTermId: builder.query({
      query: (data: {
        TermId: number;
        IsCombo?: boolean;
        IsFree?: boolean;
      }) => ({
        url: '42',
        method: 'POST',
        body: data,
      }),
      providesTags: ['CommonService'],
    }),
    studentByPackageId: builder.query({
      query: (data) => ({
        url: '43',
        method: 'POST',
        body: data,
      }),
      providesTags: ['CommonService'],
    }),
    packageByFranchiseId: builder.query({
      query: (FranchiseId: number) => ({
        url: '44',
        method: 'POST',
        body: { SelectedFranchiseId: FranchiseId },
      }),
      providesTags: ['CommonService'],
    }),
  }),
});

export const {
  useGetAllTermListQuery,
  useGetAllInternalServiceTypeListQuery,
  useGetAllCourseListQuery,
  useGetAllServiceCourseDetailTypeListQuery,
  useGetPackageListQuery,
  useGetClientLibraryLinksQuery,
  useGetClientSectionQuery,
  useGetFacultyTemplatesQuery,
  useGetParentChapterListQuery,
  useGetApiTypeListQuery,
  useGetFranchiseListQuery,
  useGetLayoutStoreListQuery,
  useGetEmailServiceProvideListQuery,
  useGetEmailServiceProviderSuggestionsQuery,
  useGetTeacherListQuery,
  useGetPackageListOfSpecificServiceQuery,
  useGetAllPlayerQuery,
  useGetAllMcqQuestionTypeQuery,
  useGetAllInternalServicesByTypeQuery,
  useGetAllTherySegmentQuery,
  useGetAllCountryCodeListQuery,
  useGetAllStoreQuery,
  useGetAllPositionQuery,
  useGetFacultyListQuery,
  useGetMediaListQuery,
  useGetAllExistingHostByFranchiseIdQuery,
  useGetAllMCQSetDropdownQuery,
  useGetDashboardCardListDropdownQuery,
  useGetNotificationTypeQuery,
  useGetLockscreenTypeQuery,
  useGetContentManagementTypeQuery,
  useGetAllCommonEmailTemplateQuery,
  useGetAllVideoTypeDropdownListQuery,
  useGetNotesTypeQuery,
  useClientDeployLinkListQuery,
  useGetPackageListWithComboQuery,
  usePackageDropdownPerCourseQuery,
  usePackageEmailTemplateQuery,
  useGetChapterByCourseIdQuery,
  useGetChapterByPackageIdQuery,
  usePackageDropdownByCourseIdQuery,
  usePackageDropdownByTermIdQuery,
  useStudentByPackageIdQuery,
  usePackageByFranchiseIdQuery,
} = SPCommonServicesApi;
export default SPCommonServicesApi;
